import {
  ButtonANTD,
  InputANTD,
  RadioANTD,
  TabsANTD,
  TextAreaANTD,
} from '@zennolab_platform/webui-kit/dist/ui/antdComponents';
import { useFormatMessage } from '@zennolab_platform/webui-sdk/dist/hooks';
import { type ReactNode, useState } from 'react';

import { LANGUAGES } from '../../constants/announcement';
import type { IAnnouncementLocalization } from '../../types/announcement';
import { Toolbar } from '../layout';
import { CtaRow, DefaultRow, Fieldset, FieldStack, Muted } from './styled';

const hasContent = (l: IAnnouncementLocalization) =>
  Boolean(l.title.trim() || l.text.trim() || l.ctaLabel?.trim() || l.ctaUrl?.trim());

/** Title and text per language as one tab per language, with one default-language switch; shared by the
 * announcement and version card forms. A language is sent once it has content or is the default.
 * `cta` adds the per-language button label and link (announcement form only). */
export function LocalizationsEditor({
  value,
  onChange,
  readOnly,
  cta = false,
}: {
  value: IAnnouncementLocalization[];
  onChange: (value: IAnnouncementLocalization[]) => void;
  readOnly: boolean;
  cta?: boolean;
}) {
  const { t } = useFormatMessage();
  const defaultLanguage = value.find(l => l.isDefault)?.language ?? value[0]?.language ?? LANGUAGES[0];
  const [active, setActive] = useState<string>(defaultLanguage);
  const rowOf = (language: string): IAnnouncementLocalization =>
    value.find(l => l.language === language) ?? { language, title: '', text: '', isDefault: false };

  /** Keeps languages in LANGUAGES order and drops empty non-default ones. */
  const commit = (rows: IAnnouncementLocalization[]) =>
    onChange(
      LANGUAGES.map(lang => rows.find(l => l.language === lang)).filter(
        (l): l is IAnnouncementLocalization => l !== undefined && (l.isDefault || hasContent(l)),
      ),
    );

  const update = (language: string, patch: Partial<IAnnouncementLocalization>) => {
    const row = { ...rowOf(language), ...patch };
    commit([...value.filter(l => l.language !== language), row]);
  };

  const setDefault = (language: string) => {
    const rows = LANGUAGES.map(lang => ({ ...rowOf(lang), isDefault: lang === language }));
    commit(rows);
    setActive(language);
  };

  const field = (language: string, id: string, label: string, control: ReactNode) => (
    <label htmlFor={`${id}-${language}`}>
      {label}
      {control}
    </label>
  );

  const items = LANGUAGES.map(language => {
    const row = rowOf(language);
    return {
      key: language,
      label: (
        <span>
          {language.toUpperCase()}
          {row.isDefault ? (
            <Muted>
              {' '}
              ·{' '}
              {t({
                id: 'announcements.form.default',
                defaultMessage: 'По умолчанию',
                description: 'Пометка языка по умолчанию во вкладке языка',
              })}
            </Muted>
          ) : (
            !hasContent(row) && (
              <Muted>
                {' '}
                ·{' '}
                {t({
                  id: 'announcements.form.languageEmpty',
                  defaultMessage: 'не заполнен',
                  description: 'Пометка незаполненного языка во вкладке языка',
                })}
              </Muted>
            )
          )}
        </span>
      ),
      children: (
        <FieldStack>
          {field(
            language,
            'title',
            t({ id: 'announcements.form.title', defaultMessage: 'Заголовок', description: 'Поле локализации анонса' }),
            <InputANTD
              id={`title-${language}`}
              disabled={readOnly}
              value={row.title}
              onChange={e => update(language, { title: e.target.value })}
            />,
          )}
          {field(
            language,
            'text',
            t({ id: 'announcements.form.text', defaultMessage: 'Текст', description: 'Поле локализации анонса' }),
            <TextAreaANTD
              id={`text-${language}`}
              disabled={readOnly}
              autoSize={{ minRows: 4, maxRows: 12 }}
              value={row.text}
              onChange={e => update(language, { text: e.target.value })}
            />,
          )}
          {cta && (
            <CtaRow>
              {field(
                language,
                'cta-label',
                t({
                  id: 'announcements.form.ctaLabel',
                  defaultMessage: 'Текст кнопки',
                  description: 'Поле локализации анонса',
                }),
                <InputANTD
                  id={`cta-label-${language}`}
                  disabled={readOnly}
                  value={row.ctaLabel ?? ''}
                  onChange={e => update(language, { ctaLabel: e.target.value })}
                />,
              )}
              {field(
                language,
                'cta-url',
                t({
                  id: 'announcements.form.ctaUrl',
                  defaultMessage: 'Ссылка кнопки',
                  description: 'Поле локализации анонса',
                }),
                <InputANTD
                  id={`cta-url-${language}`}
                  disabled={readOnly}
                  placeholder="https://"
                  value={row.ctaUrl ?? ''}
                  onChange={e => update(language, { ctaUrl: e.target.value })}
                />,
              )}
            </CtaRow>
          )}
          {!readOnly && !row.isDefault && hasContent(row) && (
            <Toolbar>
              <ButtonANTD
                type="link"
                danger
                onClick={() => update(language, { title: '', text: '', ctaLabel: '', ctaUrl: '' })}
              >
                {t({
                  id: 'announcements.form.clearLanguage',
                  defaultMessage: 'Очистить язык',
                  description: 'Кнопка очистки текстов языка',
                })}
              </ButtonANTD>
            </Toolbar>
          )}
        </FieldStack>
      ),
    };
  });

  return (
    <Fieldset>
      <legend>
        {t({ id: 'announcements.form.content', defaultMessage: 'Содержание', description: 'Раздел формы анонса' })}
      </legend>
      <DefaultRow>
        <span id="default-language">
          {t({
            id: 'announcements.form.defaultLanguage',
            defaultMessage: 'Язык по умолчанию',
            description: 'Подпись переключателя языка по умолчанию',
          })}
        </span>
        <RadioANTD.Group
          aria-labelledby="default-language"
          optionType="button"
          buttonStyle="solid"
          disabled={readOnly}
          value={defaultLanguage}
          options={LANGUAGES.map(lang => ({ label: lang.toUpperCase(), value: lang }))}
          onChange={e => setDefault(e.target.value as string)}
        />
      </DefaultRow>
      <TabsANTD activeKey={active} onChange={setActive} items={items} />
    </Fieldset>
  );
}
